import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";
import { useCustomerStore } from "../../../../../store/customerStore";
import { ADD_CUSTOMER_STATUS } from "../../../../../gqls/customer";
import { CUSTOMER_STATUS_FIELDS } from "../../../../../fragments/customer";
import generatePastelColor from "../../../../../utils/genPastelColor";
import { blackButtonContained } from "../../../../../classPresets";

const schema = z.object({
  name: z.string().min(1, { message: "required" }),
  description: z.string().min(1, { message: "required" }),
  color: z.string().min(1, { message: "required" }),
});

export default function NewCustomerStat() {
  const [adding, setAdding] = useState(false);

  const { statuses, addStatus } = useCustomerStore();

  const defaultValues = {
    name: "",
    description: "",
    color: generatePastelColor(),
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors, touchedFields },
  } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const [insert, { loading }] = useMutation(ADD_CUSTOMER_STATUS);

  const onSubmit = handleSubmit(data => {
    insert({
      variables: {
        object: {
          ...data,
          priority: statuses.length,
        },
      },
      onError(error) {
        console.log(error);
      },
      onCompleted(data) {
        addStatus(data.insert_customerStatuses_one);
        setAdding(false);
        reset({
          ...defaultValues,
          color: generatePastelColor(),
        });
      },
      update(cache, { data: { insert_customerStatuses_one: newStat } }) {
        cache.modify({
          fields: {
            customerStatuses(existing = []) {
              const newStatRef = cache.writeFragment({
                data: newStat,
                fragment: CUSTOMER_STATUS_FIELDS,
              });
              return [...existing, newStatRef];
            },
          },
        });
      },
    });
  });

  return (
    <>
      <Button
        {...blackButtonContained}
        onClick={() => {
          setAdding(true);
        }}
      >
        상태 추가
      </Button>

      <Dialog
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
      >
        <DialogTitle>고객 상태 추가</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-2">
            <TextField
              margin="dense"
              size="small"
              fullWidth
              error={errors.name && touchedFields.name}
              color="success"
              placeholder={
                errors.name?.message ? errors.name?.message : "상태 이름"
              }
              label="이름"
              {...register("name")}
            />

            <TextField
              margin="dense"
              size="small"
              fullWidth
              error={errors.description && touchedFields.description}
              color="success"
              placeholder={
                errors.description?.message
                  ? errors.description?.message
                  : "설명"
              }
              label="설명"
              {...register("description")}
            />
            <TextField
              margin="dense"
              size="small"
              fullWidth
              error={errors.color && touchedFields.color}
              color="success"
              placeholder={
                errors.color?.message ? errors.color?.message : "색상"
              }
              label="색상"
              type="color"
              defaultValue="#ffffff"
              {...register("color")}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color="success"
            onClick={() => {
              setAdding(false);
            }}
          >
            취소
          </Button>
          <LoadingButton
            disabled={!isDirty}
            loading={loading}
            variant="contained"
            color="success"
            onClick={onSubmit}
          >
            추가
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
